/**
 * Saving a file the API generated.
 *
 * Exports cannot be a plain link: every request carries the signed-in user's
 * access token, and an `<a href>` sends none - it would arrive unauthenticated
 * and come back a 401. So the file is fetched like any other call and handed to
 * the browser from memory here, which also means a failed export surfaces as the
 * server's own message rather than as a downloaded error page.
 */

/** A file the server generated, named by the server. */
export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

/**
 * Read the filename out of a `Content-Disposition` header.
 *
 * The server names its own exports - after the document, or after how many there
 * were - so that name is preferred over anything the client could reconstruct.
 * `filename*` is read first because it is the encoded form, which is what a name
 * with non-ASCII characters arrives as.
 *
 * @returns The filename, or null if the header is absent or names nothing.
 */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;

  const encoded = /filename\*=(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim()) || null;
    } catch {
      // A malformed encoding is not worth failing a download over; the plain
      // `filename` below, or the caller's fallback, still names the file.
    }
  }

  const plain = /filename="?([^";]+)"?/.exec(header);
  return plain ? plain[1].trim() || null : null;
}

/**
 * Hand a fetched file to the browser's downloads.
 *
 * The object URL is revoked once the click has been dispatched: it holds the
 * whole file in memory, and an export of a large selection is not small.
 */
export function saveFile({ blob, filename }: DownloadedFile): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
